/**
 * MobileLayout - Layout chính cho giao diện mobile
 * 
 * Cấu trúc:
 * 1. Header (cố định trên cùng)
 * 2. Main content (scrollable)
 * 3. Bottom Navigation (cố định dưới cùng)
 * 
 * TODO:
 * - Tách route conditions thành custom hook useRouteConfig
 * - Tách menu config ra file riêng src/configs/navigation.ts
 * - Thêm React.memo() để tối ưu performance
 * - Thêm transition animation khi chuyển page
 * - Thêm ErrorBoundary để xử lý lỗi
 * - Thêm Suspense và loading state
 */

// ================== IMPORTS ==================
import React, { useState } from 'react'
import { useLocation, Outlet } from 'react-router-dom'
import {
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  Squares2X2Icon,
  UserIcon,
} from '@heroicons/react/24/outline'
import { Header } from './layout/Header'
import { BottomNavigation } from './layout/BottomNavigation'
import { BottomMenuItem } from './layout/types'
import type { ScreenName } from './layout/types'
import { usePopup } from '../contexts/PopupContext'
import { ChatbotButton } from './ChatbotButton'

// ================== CONFIGURATION ==================
/**
 * Cấu hình menu chính cho bottom navigation
 * TODO: Chuyển ra file config riêng src/configs/navigation.ts
 */
const bottomMenu: BottomMenuItem[] = [
  {
    path: '/customers',
    label: 'Khách hàng',
    icon: UserGroupIcon
  },
  {
    path: '/messages',
    label: 'Tin nhắn',
    icon: ChatBubbleLeftRightIcon,
    unreadCount: 2
  },
  {
    path: '/workspace',
    label: 'Công việc',
    icon: Squares2X2Icon
  },
  {
    path: '/account',
    label: 'Tài khoản',
    icon: UserIcon
  }
]

// ================== UTILITIES ==================
/**
 * Xác định tên màn hình hiện tại dựa vào pathname
 * TODO: Chuyển thành utility function trong src/utils/routes.ts
 */
const getScreenName = (pathname: string): ScreenName => {
  if (pathname === '/' || pathname === '/workspace') return 'Công việc'
  if (pathname.startsWith('/customers')) return 'Khách hàng'
  if (pathname.startsWith('/messages')) return 'Tin nhắn'
  if (pathname.startsWith('/account')) return 'Tài khoản'
  return 'Công việc'
}

// ================== MAIN COMPONENT ==================
/**
 * MobileLayout - Layout chính cho mobile view
 * 
 * Props:
 * - Không có props vì đây là layout chính
 * 
 * State:
 * - isAnyPopupOpen: boolean - Trạng thái popup
 * 
 * Context:
 * - usePopup: Quản lý popup
 * - useLocation: Lấy thông tin route hiện tại
 */
export const MobileLayout: React.FC = () => {
  // ================== HOOKS ==================
  const location = useLocation()
  const { openCount } = usePopup()
  const [isAnyPopupOpen, setIsAnyPopupOpen] = useState(false)

  // ================== ROUTE CONDITIONS ==================
  /**
   * Các điều kiện để ẩn/hiện UI dựa vào route
   * TODO: Tách thành custom hook useRouteConfig
   */
  const isChat = location.pathname.startsWith('/messages/chat/')
  const isAIAlert = location.pathname === '/messages/ai-alert'
  const isCustomerPage = location.pathname.startsWith('/customers/')
  const isAIMessages = location.pathname === '/messages/ai'
  const isInternalMessages = location.pathname === '/messages/internal'
  const isPerformancePage = location.pathname === '/performance'
  const isGroup = location.pathname.startsWith('/messages/group/')

  // ================== UI LOGIC ==================
  const shouldHideUI = isChat || isAIAlert || isCustomerPage || isAIMessages || isInternalMessages || isPerformancePage || isGroup
  const currentScreen = getScreenName(location.pathname)

  // ================== RENDER ==================
  return (
    <div className="flex flex-col">
      {/* Header - Cố định trên cùng */}
      <Header
        shouldHideUI={shouldHideUI}
        currentScreen={currentScreen}
        isSticky={true}
      />

      {/* Main Content - Scrollable */}
      <main className="flex-1">
        <Outlet context={{ setIsAnyPopupOpen }} />
      </main>
      <ChatbotButton position="bottom-right" />

      {/* Bottom Navigation - Cố định dưới cùng */}
      <BottomNavigation
        shouldHideUI={openCount > 0}
        location={location}
        menuItems={bottomMenu}
      />
    </div>
  )
}

export default MobileLayout 